// src/pages/customer/dispensary/customer-detailsForm.jsx
"use client";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import Navbar from "../../../components/navbar";

import Footer from "../../../components/footer";
import Table from "../../../components/Table";
import NameSearchFilter from "../../../components/name-search-filter";
import Pagination from "../../../components/Pagination";
import Button from "../../../components/Button";
import styles from "../css/customer-details.module.css";
import { fetchPatients } from "../../../api/dispensary/script/processScriptApi.js";
import CustomerSelectionModal from "../../Dispensary/Script/views/CustomerSelection.jsx";

const ITEMS_PER_PAGE = 5;

export default function CustomerDetails({
  actionButtonLabel = "View",
  onActionClick,
  showAddPatientButton = false,
  showTitle = true,
  titleText = "Customer Details",
  headerContent = null,
  familyModalOnView = false,
  disableActionForProfile,
  filterProfiles,
  fetchRows,
  columns: columnsProp,
  searchPlaceholder = "Search by surname...",
  searchLabelText = "Filter",
  loadingText = "Loading profiles...",
  emptyText = "No profiles found.",
  backTo,
  onBack,
}) {

  const navigate = useNavigate();
  const location = useLocation();
  const { scriptFile } = location.state || {};
  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [familyProfileNo, setFamilyProfileNo] = useState(null);

  // Fetch patients — no /api prefix
  useEffect(() => {
    const fetchPatientsAsync = async () => {
      try {
        setLoading(true);
        if (typeof fetchRows === "function") {
          const rows = await fetchRows(searchTerm);
          setData(Array.isArray(rows) ? rows : []);
          return;
        }

        const patients = await fetchPatients(searchTerm);

        // Use profilenumber directly as string/number
        const formatted = patients.map((p) => ({
          profileNo: p.profilenumber, // ← Use raw number
          surname: p.surname,
          initial: p.initial,
          title: p.title,
          address: p.address,
          status: p.status,
        }));

        console.log("Formatted Patients:", formatted); // ← DEBUG
        setData(formatted);
      } catch (err) {
        console.error("Failed to load patients:", err);
        alert("Could not load patient profiles.");
      } finally {
        setLoading(false);
      }
    };

    fetchPatientsAsync();
  }, [searchTerm, fetchRows]);

  // Action button handler
  const handleActionClick = (profileNo) => {
    if (onActionClick) return onActionClick(profileNo);

    const isPriceScriptSubmitFlow =
      location.pathname === "/customer/dispensary/script/submit-claim/customer-details";
    if (isPriceScriptSubmitFlow) {
      setFamilyProfileNo(profileNo);
      return;
    }

    if (familyModalOnView && actionButtonLabel === "View") {
      setFamilyProfileNo(profileNo);
      return;
    }

    if (actionButtonLabel === "View" && showAddPatientButton) {
      navigate(`/customer/dispensary/otc-direct/dispense-table`);
    } else if (actionButtonLabel === "Rewrite Script") {
      navigate(`/customer/dispensary/script/customer-details/${profileNo}/rewrite-patient-script`);
    } else {
      navigate(`/customer/dispensary/script/customer-details/${profileNo}/filter-patient-script`);
    }
  };

  const handleAddPatient = () => {
    navigate("/customer/profile/create", { state: { openAdd: true } });
  };

  // Table columns
  const columns = [
    { header: "Profile No.", accessor: "profileNo" },
    { header: "Surname", accessor: "surname" },
    { header: "Initial", accessor: "initial" },
    { header: "Title", accessor: "title" },
    { header: "Address", accessor: "address" },
    { header: "Status", accessor: "status" },
    {
      header: "Action",
      render: (_, row) => (
        <Button
          variant="primary"
          disabled={typeof disableActionForProfile === 'function' ? disableActionForProfile(row.profileNo) : false}
          onClick={() => handleActionClick(row.profileNo)}
        >
          {actionButtonLabel}
        </Button>
      ),
    },
  ];

  const effectiveColumns = Array.isArray(columnsProp) && columnsProp.length > 0 ? columnsProp : columns;

  // Search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Optional external filter before pagination
  const effectiveData = typeof filterProfiles === 'function' ? data.filter(filterProfiles) : data;

  // Pagination
  const totalPages = Math.ceil(effectiveData.length / ITEMS_PER_PAGE);
  const paginatedData = effectiveData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className={styles.container}>
      <Navbar backTo={backTo} onBack={onBack} />

      <main className={styles.main}>
        {showTitle && <h1 className={styles.title}>{titleText}</h1>}
        {headerContent && <div className={styles.headerContent}>{headerContent}</div>}

        <div className={styles.filterWrapper}>
          <NameSearchFilter
            onChange={handleSearch}
            placeholder={searchPlaceholder}
            labelText={searchLabelText}
          />
          {showAddPatientButton && (
            <button className={styles.addPatientButton} onClick={handleAddPatient}>
              Add Patient
            </button>
          )}
        </div>

        {loading ? (
          <p className={styles.loading}>{loadingText}</p>
        ) : effectiveData.length === 0 ? (
          <p className={styles.noData}>{emptyText}</p>
        ) : (
          <>
            <Table columns={effectiveColumns} data={paginatedData} />
            <div className={styles.paginationWrapper}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </>
        )}
      </main>

      <Footer />
      {familyProfileNo && (
        <CustomerSelectionModal
          isOpen={!!familyProfileNo}
          onClose={() => setFamilyProfileNo(null)}
          profileNo={familyProfileNo}
          scriptFile={scriptFile}
        />
      )}
    </div>
  );
}